import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import GenieLoader from "@/components/ui/GenieLoader";
import { useGenieAccess } from "@/hooks/billing/useAccessLimits";
import { useRole } from "@/hooks/auth/useRole";
import { Roles } from '@/types/auth/auth';

interface GenieAccessRouteProps {
  children: React.ReactNode;
}

export default function GenieAccessRoute({ children }: GenieAccessRouteProps) {
  const { canCreateGenie, isLoading, requiresUpgrade } = useGenieAccess();
  const { hasRole } = useRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !canCreateGenie && !hasRole(Roles.Admin)) {
      if (requiresUpgrade) {
        // Redirect to pricing/billing if they need to upgrade
        navigate("/pricing", { replace: true });
      } else {
        // Redirect back if at limit but subscribed (shouldn't happen normally)
        navigate("/genie-hub/my-genies", { replace: true });
      }
    }
  }, [isLoading, canCreateGenie, requiresUpgrade, hasRole, navigate]);

  if (isLoading) {
    return <GenieLoader size="fullscreen" />;
  }

  // Admins always have access
  if (hasRole(Roles.Admin)) {
    return <>{children}</>;
  }

  // If user can't create genie, navigation will handle redirect via useEffect
  if (!canCreateGenie) {
    return null;
  }

  return <>{children}</>;
}